// TERRA mesh — turns the height/hazard grids into renderable geometry:
//  • the ARENA mesh: one vertex per field grid node (mesh height ≡ nav height ≡ unit feet — zero
//    bilinear-vs-triangle mismatch), vertex-colored with the biome's macro palette, aGlow/aPad
//    attributes feeding the terra material;
//  • a coarse SKIRT plane (radial-LOD warped, analytic far heights) running to the fog;
//  • biome dressing: garden water plane + instanced tree groves (2 draw calls);
//  • the built pads: corner posts.

import * as THREE from "three";
import { CELL } from "../config";
import { terraMaterial, type TerraMaterialHandle } from "./material";
import { buildHeightTex } from "./heighttex";
import { buildWater, type WaterHandle } from "./water";
import { buildGrass, type GrassHandle } from "./grass";
import { cloudParamsForStyle } from "../env/clouds";
import { GRID, HAZ_SLICK, type TerraLevel } from "./field";
import { fbm, smoothstep, clamp01 } from "./noise";

export interface TerraMeshResult {
  group: THREE.Group;
  handle: TerraMaterialHandle;
  mesh: THREE.Mesh; // the arena mesh (carries the terra material) — used as the GPU-cost probe
  skirt: THREE.Mesh; // the horizon skirt — its own GPU-cost probe
  water: WaterHandle | null; // garden animated water surface (null off-garden) — ticked per frame
  grass: GrassHandle | null; // garden 3D grass (null off-garden) — ticked per frame
}

// macro palette: color a vertex from (style, height, hazard). Only the ELEVATION-based bands are baked
// per-vertex now; the crisp diagonal/curved FEATURE edges (lava channels, garden shoreline) are drawn
// PER-PIXEL in the material from the interpolated feature field (vGlow) + height, so they don't staircase
// on the 50-stud grid. Fragment adds mottling/cracks/detail on top.
function vertColor(style: number, h: number, hz: number, out: THREE.Color): void {
  const y = h - 200; // 0 = pad plane
  if (style === 0) {
    // GARDEN: seabed/beach → grass → rock → snowline (the waterline itself is a per-pixel shoreline)
    if (y < -14) out.setRGB(0.62, 0.56, 0.38); // beach sand (also the seabed base under the water tint)
    else if (y < 90) out.setRGB(0.20, 0.36, 0.16); // grass
    else if (y < 210) out.setRGB(0.32, 0.28, 0.20); // rock
    else out.setRGB(0.78, 0.82, 0.85); // snow caps
  } else if (style === 1) {
    // ROCKY: dark maria lows → ochre highlands → pale mesa tops
    if (y < -20) out.setRGB(0.16, 0.10, 0.06);
    else if (y < 120) out.setRGB(0.35, 0.21, 0.10);
    else out.setRGB(0.52, 0.36, 0.20);
  } else if (style === 2) {
    // SCORCHED: near-black basalt → scorched ochre highs (molten channels drawn per-pixel from the glow field)
    if (y < 60) out.setRGB(0.085, 0.05, 0.035);
    else out.setRGB(0.24, 0.12, 0.06);
  } else {
    // ICE: blue-white sheets, brighter frozen lakes (crevasse walls sharpened per-pixel)
    if (hz === HAZ_SLICK) out.setRGB(0.68, 0.80, 0.90);
    else if (y < -60) out.setRGB(0.30, 0.42, 0.56); // crevasse depths
    else out.setRGB(0.62, 0.70, 0.80);
  }
}

export function buildTerraMesh(t: TerraLevel, sun?: THREE.Vector3): TerraMeshResult {
  const f = t.field;
  const group = new THREE.Group();
  const n = f.n;
  const handle = terraMaterial(
    f.style,
    t.seedInt,
    new THREE.Vector2((f.minX + f.maxX) / 2, (f.minZ + f.maxZ) / 2),
    new THREE.Vector2(t.startRect.x0, t.startRect.z0), // deck seams line up with the buildable tiles
    cloudParamsForStyle(f.style),
    sun,
    f.seaY,
  );

  // ---- arena mesh: one vertex per grid node, PLUS marching-squares contour vertices inserted where a
  // feature crosses a cell (#4) so the sharpened feature edge follows the true iso-curve, not the grid.
  // Dynamic buffers (contour verts appended); non-contour cells keep the exact original 2-triangle split. ----
  const pos: number[] = [];
  const col: number[] = [];
  const aGlow: number[] = [];
  const aPad: number[] = [];
  const c = new THREE.Color();
  for (let gz = 0; gz <= n; gz++) {
    for (let gx = 0; gx <= n; gx++) {
      const i = gz * (n + 1) + gx;
      pos.push(f.minX + gx * GRID, f.heights[i], f.minZ + gz * GRID);
      vertColor(f.style, f.heights[i], f.hazards[i], c);
      col.push(c.r, c.g, c.b);
      aGlow.push(f.glow[i]);
      aPad.push(f.pad[i]);
    }
  }
  const index: number[] = [];
  // WELD map for contour vertices: the SAME edge crossing is emitted by both cells sharing the edge (and
  // several triangles within a cell) with bit-identical position/attributes — merging them lets
  // computeVertexNormals SMOOTH across the feature rim and walls instead of hard-creasing every contour
  // edge (the creases read as harsh/faceted "chunks"; the geometry itself stays crisp).
  const weld = new Map<string, number>();
  for (let gz = 0; gz < n; gz++) {
    for (let gx = 0; gx < n; gx++) {
      const a = gz * (n + 1) + gx, b = a + 1, d = a + (n + 1), e = d + 1;
      const tess = f.contourTess.get(gz * n + gx);
      if (!tess) { index.push(a, d, b, b, d, e); continue; } // standard cell (unchanged)
      // corner grid vertex indices: contour corner 0=(gx,gz)=a, 1=(gx+1,gz)=b, 2=(gx,gz+1)=d, 3=(gx+1,gz+1)=e
      const cIdx = [a, b, d, e];
      const vIndex = (vert: { u: number; v: number; refs: { c: number; w: number }[] }): number => {
        if (vert.refs.length === 1 && vert.refs[0].w === 1) return cIdx[vert.refs[0].c]; // reuse the grid vertex
        const wx = f.minX + (gx + vert.u) * GRID, wz = f.minZ + (gz + vert.v) * GRID;
        const key = wx.toFixed(2) + "," + wz.toFixed(2);
        const seen = weld.get(key);
        if (seen !== undefined) return seen;
        // NEW contour vertex: interpolate height/color/glow/pad from its corner refs
        let hy = 0, r = 0, g = 0, bl = 0, gw = 0, pd = 0;
        for (const rf of vert.refs) {
          const ci = cIdx[rf.c];
          hy += f.heights[ci] * rf.w;
          r += col[ci * 3] * rf.w; g += col[ci * 3 + 1] * rf.w; bl += col[ci * 3 + 2] * rf.w;
          gw += aGlow[ci] * rf.w; pd += aPad[ci] * rf.w;
        }
        const ni = pos.length / 3;
        pos.push(wx, hy, wz);
        col.push(r, g, bl); aGlow.push(gw); aPad.push(pd);
        weld.set(key, ni);
        return ni;
      };
      for (const tri of tess) index.push(vIndex(tri[0]), vIndex(tri[1]), vIndex(tri[2]));
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setIndex(new THREE.BufferAttribute(new Uint32Array(index), 1));
  geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(pos), 3));
  geo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(col), 3));
  geo.setAttribute("aGlow", new THREE.BufferAttribute(new Float32Array(aGlow), 1));
  geo.setAttribute("aPad", new THREE.BufferAttribute(new Float32Array(aPad), 1));
  geo.computeVertexNormals();
  const arena = new THREE.Mesh(geo, handle.material);
  arena.receiveShadow = true;
  arena.castShadow = true; // ridges/mesas throw real shadows across the field
  group.add(arena);

  // ---- skirt: coarse radially-warped plane to the fog, analytic far heights, tucked under the arena ----
  const SK_SIZE = 72000, SK_SEG = 56, LOD_POWER = 2.2;
  const sgeo = new THREE.PlaneGeometry(SK_SIZE, SK_SIZE, SK_SEG, SK_SEG);
  sgeo.rotateX(-Math.PI / 2);
  const spos = sgeo.getAttribute("position") as THREE.BufferAttribute;
  const scol = new Float32Array(spos.count * 3);
  const sglow = new Float32Array(spos.count); // zeros
  const spad = new Float32Array(spos.count); // zeros
  const halfSk = SK_SIZE / 2;
  const fieldHalf = (f.maxX - f.minX) / 2;
  let globalMin = Infinity; // deepest arena height — the safe under-tuck level for inside skirt verts
  for (let i = 0; i < f.heights.length; i++) if (f.heights[i] < globalMin) globalMin = f.heights[i];
  for (let i = 0; i < spos.count; i++) {
    let lx = spos.getX(i), lz = spos.getZ(i);
    const ch = Math.max(Math.abs(lx), Math.abs(lz)) / halfSk;
    if (ch > 1e-6) {
      const s = Math.pow(ch, LOD_POWER) / ch;
      lx *= s; lz *= s;
      spos.setX(i, lx); spos.setZ(i, lz);
    }
    const inside = Math.max(Math.abs(lx), Math.abs(lz)) < fieldHalf;
    const far = smoothstep(fieldHalf * 1.6, fieldHalf * 3.6, Math.hypot(lx, lz));
    // inside the arena the vertex is dropped BELOW the deepest arena point (so the coarse skirt chords
    // stay hidden under the arena, review) — its heightAt/fbm would just be overwritten, so skip them.
    let y;
    if (inside) {
      y = globalMin - 25;
    } else {
      y = f.heightAt(lx, lz) * (1 - far) + 150 * far; // flatten into the fog
      y += (fbm(lx / 5200, lz / 5200, t.seedInt & 0xffff, 3) - 0.5) * 700 * far; // big far mounds
    }
    spos.setY(i, y);
    vertColor(f.style, y, 0, c);
    const dim = 1 - far * 0.25;
    scol[i * 3] = c.r * dim; scol[i * 3 + 1] = c.g * dim; scol[i * 3 + 2] = c.b * dim;
  }
  sgeo.setAttribute("color", new THREE.BufferAttribute(scol, 3));
  sgeo.setAttribute("aGlow", new THREE.BufferAttribute(sglow, 1));
  sgeo.setAttribute("aPad", new THREE.BufferAttribute(spad, 1));
  // Drop the skirt's INNER DISC: every triangle fully inside the field sits UNDER the arena that already
  // covers it, so it only cost wasted overdraw (full terra shader, drawn first via renderOrder -1) + shadow
  // sampling — never visible. Keep the ring (any triangle touching/beyond the field edge) so the horizon and
  // the seam against the arena are unchanged.
  {
    const sidx = sgeo.getIndex();
    if (sidx) {
      const inside = new Uint8Array(spos.count);
      for (let i = 0; i < spos.count; i++) inside[i] = Math.max(Math.abs(spos.getX(i)), Math.abs(spos.getZ(i))) < fieldHalf ? 1 : 0;
      const kept: number[] = [];
      for (let ti = 0; ti < sidx.count; ti += 3) {
        const a = sidx.getX(ti), b = sidx.getX(ti + 1), cx = sidx.getX(ti + 2);
        if (inside[a] && inside[b] && inside[cx]) continue;
        kept.push(a, b, cx);
      }
      sgeo.setIndex(kept);
    }
  }
  sgeo.computeVertexNormals();
  const skirt = new THREE.Mesh(sgeo, handle.material);
  // NOT receiveShadow: the skirt is the filler BEYOND the arena and the sun's shadow camera is fitted to the
  // arena, so every skirt pixel paid a 1536² shadow-map sample + PCF to be told it was lit. Note this shares the
  // arena's material, so it is the RECEIVER FLAG doing the work here, not a shader variant — a stripped skirt
  // material turned out to be worth almost nothing, because the expensive detail layers (grain, cracks,
  // mottling, relief) are already branch-skipped by their own px/footprint gates at skirt distance.
  skirt.receiveShadow = false;
  skirt.renderOrder = -1;
  group.add(skirt);

  // ---- garden dressing: one shared heightmap → animated water surface + 3D grass + tree groves ----
  let water: WaterHandle | null = null;
  let grass: GrassHandle | null = null;
  if (f.style === 0) {
    const ht = buildHeightTex(f); // R = ground height, A = grass-band mask — shared by water + grass
    water = buildWater(f, ht.tex, ht.hMin, ht.hMax, sun);
    group.add(water.mesh);
    grass = buildGrass(f, ht.tex, ht.hMin, ht.hMax, sun, t.seedInt, cloudParamsForStyle(f.style));
    group.add(grass.mesh);
    addGroves(group, t);
  }

  // ---- built pads: corner posts + objective visuals ----
  const postMat = new THREE.MeshStandardMaterial({ color: 0x2b3138, emissive: 0x3fd0e6, emissiveIntensity: 0.5, roughness: 0.6 });
  const postGeo = new THREE.CylinderGeometry(9, 12, 120, 6);
  const posts = new THREE.InstancedMesh(postGeo, postMat, 8);
  const m4 = new THREE.Matrix4();
  let pi = 0;
  // pad long side is PERPENDICULAR to the battle axis — orient the corner-post offsets to match
  // (fixed offsets put posts off the deck on X-axis battles; review finding)
  const alongX = Math.abs(t.exit.x - t.start.x) >= Math.abs(t.exit.z - t.start.z);
  const postOx = alongX ? 580 : 780;
  const postOz = alongX ? 780 : 580;
  for (const which of [t.start, t.exit]) {
    const y = f.heightAt(which.x, which.z);
    for (const [sx, sz] of [[-1, -1], [-1, 1], [1, -1], [1, 1]] as const) {
      m4.setPosition(which.x + sx * postOx, y + 60, which.z + sz * postOz);
      posts.setMatrixAt(pi++, m4);
    }
  }
  posts.instanceMatrix.needsUpdate = true;
  group.add(posts);
  // start marker + enemy-core objective removed — base-building replaces the "reach the core" objective

  return { group, handle, mesh: arena, skirt, water, grass };
}

/** Instanced low-poly tree groves on flat walkable garden ground (visual cover, no collision). */
function addGroves(group: THREE.Group, t: TerraLevel): void {
  const f = t.field;
  const spots: Array<{ x: number; z: number; y: number; s: number }> = [];
  const sd = t.seedInt & 0xffff;
  for (const [k] of t.nav.cost ?? []) {
    if (spots.length >= 320) break;
    const [cx, cz] = k.split(",").map(Number);
    const wx = (cx + 0.5) * CELL, wz = (cz + 0.5) * CELL;
    if (Math.hypot(wx - t.start.x, wz - t.start.z) < 1100 || Math.hypot(wx - t.exit.x, wz - t.exit.z) < 1100) continue;
    const grove = fbm(wx / 1600 + sd, wz / 1600 - sd, sd + 87, 3);
    if (grove < 0.62) continue;
    const nTree = 1 + ((cx * 31 + cz * 17) % 3);
    for (let i = 0; i < nTree && spots.length < 320; i++) {
      const ox = ((((cx * 73 + i * 37) % 17) / 17) - 0.5) * CELL * 0.9;
      const oz = ((((cz * 59 + i * 41) % 19) / 19) - 0.5) * CELL * 0.9;
      const x = wx + ox, z = wz + oz;
      const h = f.heightAt(x, z);
      if (h < 195) continue; // not in water
      spots.push({ x, z, y: h, s: 0.75 + (((cx + cz + i) % 7) / 7) * 0.6 });
    }
  }
  if (!spots.length) return;
  const trunkGeo = new THREE.CylinderGeometry(6, 9, 60, 5);
  const trunkMat = new THREE.MeshStandardMaterial({ color: 0x4a3520, roughness: 0.95 });
  const canGeo = new THREE.ConeGeometry(52, 150, 6);
  const canMat = new THREE.MeshStandardMaterial({ color: 0x2a4a1e, roughness: 0.9, flatShading: true });
  const trunks = new THREE.InstancedMesh(trunkGeo, trunkMat, spots.length);
  const cans = new THREE.InstancedMesh(canGeo, canMat, spots.length);
  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const sc = new THREE.Vector3();
  const p = new THREE.Vector3();
  const UP = new THREE.Vector3(0, 1, 0); // constant axis — allocate once, not per tree
  for (let i = 0; i < spots.length; i++) {
    const s = spots[i];
    sc.setScalar(s.s);
    q.setFromAxisAngle(UP, (i * 2.39996) % (Math.PI * 2));
    p.set(s.x, s.y + 30 * s.s, s.z);
    m4.compose(p, q, sc);
    trunks.setMatrixAt(i, m4);
    p.set(s.x, s.y + (60 + 70) * s.s, s.z);
    m4.compose(p, q, sc);
    cans.setMatrixAt(i, m4);
  }
  trunks.instanceMatrix.needsUpdate = true;
  cans.instanceMatrix.needsUpdate = true;
  trunks.castShadow = cans.castShadow = true;
  const cap = clamp01(spots.length / 320);
  void cap;
  group.add(trunks, cans);
}
